import type { EditorElement, AdAnimation, AspectRatio } from '@/pages/editor/types';

export interface VideoExportOptions {
  elements: EditorElement[];
  backgroundImageUrl: string;
  overlayOpacity: number;
  animation: AdAnimation;
  imageFit: 'cover' | 'contain';
  imagePositionX: number;
  imagePositionY: number;
  aspectRatio: AspectRatio;
}

// ── Canvas dimensions per aspect ratio ─────────────────────────────
const CANVAS_SIZES: Record<AspectRatio, { w: number; h: number }> = {
  '1:1':  { w: 800, h: 800 },
  '16:9': { w: 960, h: 540 },
  '9:16': { w: 540, h: 960 },
};

// ── Total recording duration per animation ─────────────────────────
const RECORD_DURATION: Record<AdAnimation, number> = {
  'none':        2,    // 2s static frame
  'ken-burns':   9,    // exactly 1 loop
  'cta-pulse':   4.8,  // 3 × 1.6s loops
  'text-reveal': 8,    // 2 × 4s loops
  'spotlight':   9.6,  // 3 × 3.2s loops
};

// ── Loop period per animation ──────────────────────────────────────
const LOOP_PERIOD: Record<AdAnimation, number> = {
  'none':        2,
  'ken-burns':   9,
  'cta-pulse':   1.6,
  'text-reveal': 4,
  'spotlight':   3.2,
};

// ── Utilities ──────────────────────────────────────────────────────
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function applyTextTransform(text: string, transform: string): string {
  if (transform === 'uppercase') return text.toUpperCase();
  if (transform === 'lowercase') return text.toLowerCase();
  if (transform === 'capitalize') return text.replace(/\b\w/g, (c) => c.toUpperCase());
  return text;
}

// ── Rounded-rect path helper ────────────────────────────────────────
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  if (r <= 0) { ctx.rect(x, y, w, h); return; }
  const safe = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + safe, y);
  ctx.lineTo(x + w - safe, y);
  ctx.arcTo(x + w, y, x + w, y + safe, safe);
  ctx.lineTo(x + w, y + h - safe);
  ctx.arcTo(x + w, y + h, x + w - safe, y + h, safe);
  ctx.lineTo(x + safe, y + h);
  ctx.arcTo(x, y + h, x, y + h - safe, safe);
  ctx.lineTo(x, y + safe);
  ctx.arcTo(x, y, x + safe, y, safe);
  ctx.closePath();
}

// ── Load an image (resolves even for data-URL blobs) ───────────────
async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// ── Ken Burns keyframe interpolation ──────────────────────────────
function kenBurnsState(p: number): { scale: number; tx: number; ty: number } {
  if (p < 0.33) {
    const t = p / 0.33;
    return { scale: lerp(1, 1.09, t), tx: lerp(0, -2, t), ty: lerp(0, -1, t) };
  }
  if (p < 0.66) {
    const t = (p - 0.33) / 0.33;
    return { scale: lerp(1.09, 1.06, t), tx: lerp(-2, 1.5, t), ty: lerp(-1, -0.5, t) };
  }
  const t = (p - 0.66) / 0.34;
  return { scale: lerp(1.06, 1, t), tx: lerp(1.5, 0, t), ty: lerp(-0.5, 0, t) };
}

// ── Draw background image ──────────────────────────────────────────
function drawBackground(
  ctx: CanvasRenderingContext2D,
  bgImg: HTMLImageElement,
  cw: number, ch: number,
  imageFit: 'cover' | 'contain',
  posX: number, posY: number,
  animation: AdAnimation,
  loopP: number,
): void {
  ctx.save();

  if (animation === 'ken-burns') {
    const kb = kenBurnsState(loopP);
    ctx.translate(cw / 2, ch / 2);
    ctx.scale(kb.scale, kb.scale);
    ctx.translate(-cw / 2 + (kb.tx / 100) * cw, -ch / 2 + (kb.ty / 100) * ch);
  }

  const imgAR = bgImg.naturalWidth / bgImg.naturalHeight;
  const canAR = cw / ch;
  let dw: number, dh: number;

  if (imageFit === 'cover') {
    if (imgAR > canAR) { dh = ch; dw = ch * imgAR; }
    else { dw = cw; dh = cw / imgAR; }
  } else {
    if (imgAR > canAR) { dw = cw; dh = cw / imgAR; }
    else { dh = ch; dw = ch * imgAR; }
  }

  const dx = (posX / 100) * (cw - dw);
  const dy = (posY / 100) * (ch - dh);
  ctx.drawImage(bgImg, dx, dy, dw, dh);
  ctx.restore();
}

// ── Draw spotlight sweep ───────────────────────────────────────────
function drawSpotlight(
  ctx: CanvasRenderingContext2D,
  cw: number, ch: number,
  loopP: number,
): void {
  // Sweeps from -160% to 260% over 1 loop (same as CSS version)
  const xPct = lerp(-1.6, 2.6, loopP);
  const x = xPct * cw;
  // Simulate a skewed gradient band ~40% wide
  const bandW = cw * 0.4;
  const grad = ctx.createLinearGradient(x, 0, x + bandW, 0);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.3, 'rgba(255,255,255,0.32)');
  grad.addColorStop(0.5, 'rgba(255,255,255,0.38)');
  grad.addColorStop(0.7, 'rgba(255,255,255,0.32)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, cw, ch);
  ctx.restore();
}

// ── Draw a single element ──────────────────────────────────────────
function drawElement(
  ctx: CanvasRenderingContext2D,
  el: EditorElement,
  cw: number, ch: number,
  animation: AdAnimation,
  loopP: number,
  imageCache: Map<string, HTMLImageElement>,
): void {
  if (!el.visible) return;

  const elX = (el.x / 100) * cw;
  const elY = (el.y / 100) * ch;
  const elW = el.style.width ? (el.style.width / 100) * cw : undefined;

  // ── Compute per-element animation modifiers ────────────────────
  let elAlpha = el.style.opacity;
  let elOffsetY = 0;

  if (animation === 'text-reveal' && (el.type === 'text' || el.type === 'button')) {
    // 4s loop: 0-18% fade-in+slide, 18-78% hold, 78-100% fade-out+slide-up
    if (loopP < 0.18) {
      const t = loopP / 0.18;
      elAlpha *= t;
      elOffsetY = lerp(14, 0, t);
    } else if (loopP > 0.78) {
      const t = (loopP - 0.78) / 0.22;
      elAlpha *= lerp(1, 0, t);
      elOffsetY = lerp(0, -10, t);
    }
  }

  ctx.save();
  ctx.globalAlpha = clamp(elAlpha, 0, 1);

  const drawY = elY + elOffsetY;

  // ── IMAGE element (logo) ───────────────────────────────────────
  if (el.type === 'image' && el.imageUrl) {
    const img = imageCache.get(el.imageUrl);
    if (img) {
      const w = elW ?? cw * 0.3;
      const h = (img.naturalHeight / img.naturalWidth) * w;
      const r = el.style.borderRadius;
      if (r > 0) {
        ctx.save();
        roundedRectPath(ctx, elX, drawY, w, h, r);
        ctx.clip();
      }
      ctx.drawImage(img, elX, drawY, w, h);
      if (r > 0) ctx.restore();
    }
    ctx.restore();
    return;
  }

  // ── SHAPE element ──────────────────────────────────────────────
  if (el.type === 'shape') {
    const w = elW ?? cw * 0.4;
    const h = el.style.paddingY > 0 ? el.style.paddingY * 2 + 16 : 50;
    roundedRectPath(ctx, elX, drawY, w, h, el.style.borderRadius);
    ctx.fillStyle = el.style.backgroundColor;
    ctx.fill();
    ctx.restore();
    return;
  }

  // ── TEXT / BUTTON element ──────────────────────────────────────
  const text = applyTextTransform(el.content, el.style.textTransform);
  ctx.font = `${el.style.fontWeight} ${el.style.fontSize}px ${el.style.fontFamily}`;
  if ('letterSpacing' in ctx) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing
      = `${el.style.letterSpacing}px`;
  }

  const measured = ctx.measureText(text).width;
  const padX = el.style.paddingX;
  const padY = el.style.paddingY;
  const lineH = el.style.fontSize * (el.style.lineHeight || 1.2);
  const boxW = elW ?? measured + padX * 2;
  const boxH = lineH + padY * 2;

  // Background fill (button / box)
  const bg = el.style.backgroundColor;
  if (bg && bg !== 'transparent' && bg !== 'rgba(0,0,0,0)' && bg !== 'rgba(0, 0, 0, 0)') {
    // CTA Pulse glow ring
    if (animation === 'cta-pulse' && el.type === 'button') {
      const ringP = clamp(loopP / 0.7, 0, 1);
      const ringOpacity = lerp(0.75, 0, ringP);
      const ringBlur = lerp(4, 28, ringP);
      if (loopP < 0.7 && ringOpacity > 0.01) {
        ctx.save();
        ctx.shadowColor = `rgba(249,115,22,${ringOpacity})`;
        ctx.shadowBlur = ringBlur;
        roundedRectPath(ctx, elX, drawY, boxW, boxH, el.style.borderRadius);
        ctx.fillStyle = 'rgba(0,0,0,0.001)';
        ctx.fill();
        ctx.restore();
      }
    }

    ctx.save();
    roundedRectPath(ctx, elX, drawY, boxW, boxH, el.style.borderRadius);
    ctx.fillStyle = bg;
    ctx.fill();

    if (el.style.border && el.style.border !== 'none') {
      const parts = el.style.border.split(' ');
      if (parts.length >= 3) {
        ctx.lineWidth = parseFloat(parts[0]) || 1;
        ctx.strokeStyle = parts.slice(2).join(' ');
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // Text alignment offset
  const textMaxW = elW ? elW - padX * 2 : undefined;
  let textX = elX + padX;
  if (el.style.textAlign === 'center') {
    textX = elX + boxW / 2 - measured / 2;
  } else if (el.style.textAlign === 'right') {
    textX = elX + boxW - padX - measured;
  }

  // Text shadow
  if (el.style.textShadow) {
    ctx.shadowColor = 'rgba(0,0,0,0.7)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;
  }

  ctx.fillStyle = el.style.color;
  ctx.textBaseline = 'top';
  if (textMaxW !== undefined) {
    ctx.fillText(text, textX, drawY + padY, textMaxW);
  } else {
    ctx.fillText(text, textX, drawY + padY);
  }

  ctx.restore();
}

// ── Draw a complete frame at time t ─────────────────────────────────
function drawFrame(
  ctx: CanvasRenderingContext2D,
  cw: number, ch: number,
  bgImg: HTMLImageElement,
  options: VideoExportOptions,
  loopP: number, // 0-1 within one loop period
  imageCache: Map<string, HTMLImageElement>,
): void {
  // Clear
  ctx.clearRect(0, 0, cw, ch);

  // Background image
  drawBackground(ctx, bgImg, cw, ch, options.imageFit, options.imagePositionX, options.imagePositionY, options.animation, loopP);

  // Overlay
  ctx.save();
  ctx.fillStyle = `rgba(0,0,0,${options.overlayOpacity})`;
  ctx.fillRect(0, 0, cw, ch);
  ctx.restore();

  // Spotlight sweep (above overlay, below text)
  if (options.animation === 'spotlight') {
    drawSpotlight(ctx, cw, ch, loopP);
  }

  // Elements
  for (const el of options.elements) {
    drawElement(ctx, el, cw, ch, options.animation, loopP, imageCache);
  }
}

// ── Static PNG export (single frame, no animation) ──────────────────
export async function exportStaticPng(
  options: VideoExportOptions,
): Promise<void> {
  const { w: cw, h: ch } = CANVAS_SIZES[options.aspectRatio];

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');

  // Load background image
  const bgImg = await loadImage(options.backgroundImageUrl);

  // Load overlay images (logos etc.)
  const imageCache = new Map<string, HTMLImageElement>();
  for (const el of options.elements) {
    if (el.type === 'image' && el.imageUrl) {
      try { imageCache.set(el.imageUrl, await loadImage(el.imageUrl)); } catch { /* skip */ }
    }
  }

  // Wait for fonts
  try { await document.fonts.ready; } catch { /* ignore */ }

  // Draw a single static frame (loopP = 0 = start of animation, but animation is 'none' effectively)
  drawFrame(ctx, cw, ch, bgImg, { ...options, animation: 'none' }, 0, imageCache);

  // Export as PNG blob and trigger download
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Export produced an empty image. Please try again.');

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ad-with-overlays-${Date.now()}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ── Main export function ─────────────────────────────────────────────
export async function exportAnimatedVideo(
  options: VideoExportOptions,
  onProgress?: (progress: number) => void,
): Promise<void> {
  const { w: cw, h: ch } = CANVAS_SIZES[options.aspectRatio];
  const FPS = 30;
  const duration = RECORD_DURATION[options.animation];
  const period = LOOP_PERIOD[options.animation];
  const totalFrames = Math.ceil(duration * FPS);

  // Set up canvas
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');

  // Load images
  const bgImg = await loadImage(options.backgroundImageUrl);
  const imageCache = new Map<string, HTMLImageElement>();
  for (const el of options.elements) {
    if (el.type === 'image' && el.imageUrl) {
      try { imageCache.set(el.imageUrl, await loadImage(el.imageUrl)); } catch { /* skip */ }
    }
  }

  // Wait for fonts
  try { await document.fonts.ready; } catch { /* ignore */ }

  // Set up MediaRecorder
  const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
    ? 'video/webm;codecs=vp9'
    : MediaRecorder.isTypeSupported('video/webm')
    ? 'video/webm'
    : '';
  if (!mimeType) throw new Error('Your browser does not support WebM video recording.');

  const stream = canvas.captureStream(FPS);
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

  const done = new Promise<void>((res) => { recorder.onstop = () => res(); });
  recorder.start(100); // collect in 100ms chunks

  // Render frames — use setTimeout(0) to yield between frames so the browser stays responsive
  for (let f = 0; f < totalFrames; f++) {
    const t = f / FPS;
    const loopP = (t % period) / period; // 0-1 within one cycle
    drawFrame(ctx, cw, ch, bgImg, options, loopP, imageCache);
    onProgress?.((f + 1) / totalFrames);
    // Yield every ~10 frames
    if (f % 10 === 0) await new Promise<void>((r) => setTimeout(r, 0));
  }

  recorder.stop();
  await done;

  // Download
  const blob = new Blob(chunks, { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ad-animated-${Date.now()}.webm`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
